import React, { useState, useEffect } from 'react';
import Sidebar, { MobileHeader } from './Sidebar';
import MarqueeBar from './MarqueeBar';
import OfflineIndicator from '../UI/OfflineIndicator';
import ThemeToggle from '../UI/ThemeToggle';
import { useAuth } from '../../context/AuthContext';

// Persist sidebar collapsed state across page navigations and reloads
const COLLAPSED_KEY = 'sidebarCollapsed';

const getInitialCollapsed = () => {
  try {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    return stored === 'true';
  } catch {
    return false;
  }
};

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(getInitialCollapsed);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';

  // Sync collapsed state to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // localStorage unavailable — ignore
    }
  }, [sidebarCollapsed]);

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-50 via-white to-primary-50/30">
      {/* Offline indicator — red banner at the top when the device loses internet,
          toast when it comes back. Fixed, so it floats above all pages. */}
      <OfflineIndicator />

      {/* Dark / light mode toggle — moon = switch to dark, sun = back to light.
          Fixed bottom-right so it never overlaps page header buttons;
          mobile has its own toggle in MobileHeader. */}
      <div className="hidden lg:block fixed bottom-5 right-5 z-40">
        <ThemeToggle />
      </div>

      {/* Mobile Header */}
      <MobileHeader onMenuClick={() => setSidebarOpen(true)} />

      <div className="flex">
        {/* Sidebar */}
        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          isCollapsed={sidebarCollapsed}
          onToggle={toggleSidebar}
        />

        {/* Main Content */}
        <main className={`flex-1 min-h-screen min-w-0 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
          {/* Marquee Notice Bar — shows subscription warnings & admin notices (not for SuperAdmin) */}
          {!isSuperAdmin && <MarqueeBar />}

          <div className="pt-16 lg:pt-0 w-full h-full overflow-x-hidden">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;